/**
 * Generate base parameters for event
 * @param {HTMLElement} element - Clicked element (for click triggers; retained in signature for Phase 2 extensibility)
 * @param {Object} eventDef - Event definition from backend
 * @returns {Object|null} - Trigger-type-aware base parameters, or null for unknown trigger types
 */
export function generateEventParams(element, eventDef) {
  if (eventDef.triggerType === 'click') {
    return {
      selector: eventDef.triggerConfig.selector, // no optional chaining — guard in tracker.js guarantees this exists; TypeError here is correct behavior
      page_path: window.location.pathname,
      element_text: element?.textContent?.trim().replace(/\s+/g, ' ').substring(0, 80) ?? '' // collapse whitespace before truncating
    };
  }

  if (eventDef.triggerType === 'page_opened') {
    return {
      page_path: window.location.pathname
    };
  }

  // Unknown trigger type — warn and return null; caller must handle null and skip firing
  console.warn(`[EventFoundry] Unknown triggerType "${eventDef.triggerType}" in generateEventParams — skipping.`);
  return null;
}

function resolvePath(obj, path) {
  const segments = path.replace(/\[(\d+)\]/g, '.$1').split('.')
  let current = obj
  for (const segment of segments) {
    if (current == null) return undefined
    current = current[segment]
  }
  return current
}

export function extractMappedParams(paramMappings, triggerType) {
  if (triggerType !== 'click') return {};
  if (!paramMappings || !Array.isArray(paramMappings)) return {};

  const result = {};

  for (const mapping of paramMappings) {
    try {
      if (mapping.source === 'dom') {
        const element = document.querySelector(mapping.selector);
        if (!element) continue;

        let value;
        if (mapping.attribute === 'textContent') {
          value = element.textContent?.trim();
        } else if (mapping.attribute === 'value') {
          value = element.value;
        } else if (mapping.attribute === 'href') {
          value = element.href;
        } else if (mapping.attribute.startsWith('data-')) {
          const dataKey = mapping.attribute.slice(5); // remove "data-"
          value = element.dataset[dataKey];
        }

        if (value != null) {
          result[mapping.paramKey] = String(value);
        }
      } else if (mapping.source === 'dataLayer') {
        if (!Array.isArray(window.dataLayer)) continue;
        // Search from tail (most recent) for the matching event name
        for (let i = window.dataLayer.length - 1; i >= 0; i--) {
          const entry = window.dataLayer[i];
          if (!entry || entry.event !== mapping.eventName) continue;
          const resolved = resolvePath(entry, mapping.path);
          if (resolved !== undefined && resolved !== null) {
            result[mapping.paramKey] = String(resolved);
          }
          break; // Use only the most recent matching entry
        }
      }
      // else: unknown source, skip silently
    } catch {
      // Silently skip failed extractions
    }
  }

  return Object.fromEntries(
    Object.entries(result).sort(([a], [b]) => a.localeCompare(b))
  )
}
